import type { FieldValues, Path, UseFormReturn } from "react-hook-form";
import { errorMessage, fieldErrorsFrom, isApiError } from "@/lib/api/errors";

/**
 * Turn an ApiError into form state: `validation_failed` details are a Zod tree,
 * so map them onto the fields they belong to and put anything else on the root.
 */
export function applyApiError<T extends FieldValues>(
  error: unknown,
  form: UseFormReturn<T>,
  fieldMap: Partial<Record<string, Path<T>>> = {},
): void {
  if (isApiError(error) && error.code === "validation_failed") {
    const fields = fieldErrorsFrom(error.details);
    const entries = Object.entries(fields);
    if (entries.length) {
      // A field the server names but the form doesn't have would be set and then
      // never rendered — which is how a rejected request can look like a button
      // that does nothing. Anything unmatched goes to the root instead.
      const known = new Set(Object.keys(form.getValues() ?? {}));
      const orphaned: string[] = [];

      for (const [key, message] of entries) {
        const target = (fieldMap[key] ?? key) as Path<T>;
        if (known.has(String(target).split(".")[0])) {
          form.setError(target, { type: "server", message });
        } else {
          orphaned.push(`${key}: ${message}`);
        }
      }

      if (orphaned.length) {
        form.setError("root", { type: "server", message: orphaned.join(" · ") });
      }
      return;
    }
  }

  form.setError("root", { type: "server", message: errorMessage(error) });
}

/**
 * Only ever navigate to an in-app path. An absolute or protocol-relative URL
 * from a query string is an open redirect.
 */
export function safeInternalPath(value: string | null | undefined, fallback = "/dashboard"): string {
  if (!value) return fallback;
  if (!value.startsWith("/") || value.startsWith("//")) return fallback;
  if (value.includes("\\") || /^\/+\w+:/.test(value)) return fallback;
  return value;
}
